import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import BackButton from "@components/buttons/BackButton";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import BaseErrors from "@components/fields/BaseErrors";
import CheckboxGroup from "@components/fields/CheckboxGroup";
import DateInput from "@components/fields/DateInput";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import RadioButtonGroup from "@components/fields/RadioButtonGroup";
import RichTextArea from "@components/fields/RichTextArea";
import Select from "@components/fields/Select";
import Form from "@components/forms/Form";
import HandleQuery from "@components/HandleQuery";
import Heading2 from "@components/typography/Heading2";
import {
  ClientRoleRoleEnum,
  ConsultFormatEnum,
  ConsultReportFragment as ConsultReport,
  ConsultReportUpdateDocument,
  ConsultReportUpdateMutation,
  ConsultReportUpdateMutationVariables,
  GetConsultQuery,
  GetExpertsDocument,
  GetExpertsQuery,
  GetExpertsQueryVariables,
  GetJourneyWithClientRolesQuery,
  StageEnum
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { primaryExpertRole, underEighteenOrGrieving } from "@utils/modelUtils";
import { optionsForStage } from "@utils/optionUtils";
import { dateToMDYString } from "@utils/timeUtils";

type FormData = {
  attendingClientIds: string[];
  attendingOther: string | null;
  duration: number;
  expertAttendeeId: string;
  format: ConsultFormatEnum;
  nextConsult: string | null;
  stage: StageEnum | null;
  summary: string;
};

type Props = {
  autofocus: boolean;
  consult: GetConsultQuery["consult"];
  journey: GetJourneyWithClientRolesQuery["journey"];
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const ConsultReportForm = ({ autofocus, consult, journey, onCancel, onError, onSuccess }: Props) => {
  let defaultNextConsultDate = null;
  if (journey.careProfile.nextConsult) {
    defaultNextConsultDate = dateToMDYString(journey.careProfile.nextConsult);
  }

  const consultReport = useMemo(() => consult.consultReport as ConsultReport, [consult.consultReport]);

  const attendeeIds = useMemo(() => {
    const ownerClientRole = journey.clientRoles.find(role => role.role === ClientRoleRoleEnum.OWNER);
    const attendees = consult.clientAttendees.clients
      .map(client => client.id)
      .filter(clientId => journey.clientRoles.some(role => role.client.id === clientId));
    return attendees.length ? attendees : ownerClientRole ? [ownerClientRole.client.id] : [];
  }, [consult.clientAttendees.clients, journey.clientRoles]);

  const defaultValues: FormData = {
    attendingClientIds: attendeeIds,
    attendingOther: consult?.clientAttendees.other ?? null,
    duration: consultReport.duration ?? 60,
    expertAttendeeId: consultReport.expertAttendee?.id ?? primaryExpertRole(journey.expertRoles)?.expert.id ?? "",
    format: consultReport.format ?? ConsultFormatEnum.ZOOM,
    nextConsult: defaultNextConsultDate,
    stage: journey.stage ?? StageEnum.INDEPENDENT,
    summary: consultReport.summary ?? ""
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateConsultReport] = useMutation<ConsultReportUpdateMutation, ConsultReportUpdateMutationVariables>(
    ConsultReportUpdateDocument
  );

  const getExpertsQuery = useQuery<GetExpertsQuery, GetExpertsQueryVariables>(GetExpertsDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      attendingClientIds: yup
        .array()
        .required(intl.formatMessage({ id: "errors.consultReport.clientAttendees.blank" })),
      attendingOther: yup.string().nullable().defined(),
      duration: yup
        .number()
        .required(intl.formatMessage({ id: "errors.consultReport.duration.blank" }))
        .typeError(intl.formatMessage({ id: "errors.consultReport.duration.number" }))
        .moreThan(0, intl.formatMessage({ id: "errors.consultReport.duration.tooSmall" })),
      expertAttendeeId: yup
        .string()
        .required(intl.formatMessage({ id: "errors.consultReport.expertAttendeeId.blank" })),
      format: yup
        .string()
        .oneOf(Object.values(ConsultFormatEnum))
        .required(intl.formatMessage({ id: "errors.consultReport.format.blank" })),
      nextConsult: yup.string().nullable().defined(),
      stage: underEighteenOrGrieving(journey.careProfile)
        ? yup.string().nullable().defined().oneOf(Object.values(StageEnum))
        : yup
            .string()
            .oneOf(Object.values(StageEnum))
            .required(intl.formatMessage({ id: "errors.consultReport.stage.blank" })),
      summary: yup.string().required(intl.formatMessage({ id: "errors.consultReport.summary.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateConsultReport({
      variables: {
        ...form.getValues(),
        clientAttendees: {
          clientIds: form.getValues().attendingClientIds,
          other: form.getValues().attendingOther
        },
        duration: parseInt(form.getValues().duration.toString()),
        id: consultReport.id,
        stage: underEighteenOrGrieving(journey.careProfile) ? null : form.getValues().stage
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.consultReportUpdate,
        intl,
        onError,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.consultReport"
      });
    });
  }, [updateConsultReport, form, consultReport.id, journey.careProfile, intl, onError, onSuccess]);

  const stageOptions = useMemo(() => optionsForStage(intl), [intl]);

  const formatOptions = Object.values(ConsultFormatEnum).map(option => ({
    label: intl.formatMessage({ id: `models.consult.format.${option}` }),
    value: option
  }));

  const attendeeOptions = useMemo(
    () =>
      journey.clientRoles.map(option => ({
        label: option.client.fullName,
        value: option.client.id
      })),
    [journey.clientRoles]
  );

  return (
    <HandleQuery query={getExpertsQuery}>
      {expertsData => {
        const expertOptions =
          expertsData.experts.edges?.map(expert => ({
            label: expert?.node?.fullName ?? "",
            lastName: expert?.node?.lastName ?? "",
            value: expert?.node?.id ?? ""
          })) ?? [];
        const sortedExpertOptions = expertOptions.sort((a, b) =>
          a.lastName.toLowerCase() > b.lastName.toLowerCase() ? 1 : -1
        );

        return (
          <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
            <BackButton />
            <div className="ion-margin-vertical">
              <Heading2>
                <FormattedMessage
                  id="models.consultReport.forConsultOn"
                  values={{ startTime: <DateAndTimeStamp value={consult.startTime} /> }}
                />
              </Heading2>
            </div>
            <BaseErrors errors={baseErrors} />
            <Input<FormData>
              autofocus={autofocus}
              error={form.formState.errors.duration}
              label={intl.formatMessage({ id: "forms.consultReport.durationLabel" })}
              register={form.register("duration")}
              required
              type="number"
            />
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.expertAttendeeId}
              label={intl.formatMessage({ id: "forms.consultReport.expertAttendeeIdLabel" })}
              name="expertAttendeeId"
              options={sortedExpertOptions}
              required
            />
            <div className={underEighteenOrGrieving(journey.careProfile) ? "ion-hide" : undefined}>
              <RadioButtonGroup<FormData>
                control={form.control}
                error={form.formState.errors.stage}
                label={intl.formatMessage({ id: "forms.consultReport.stageLabel" })}
                name="stage"
                options={stageOptions}
                required
              />
            </div>
            <CheckboxGroup<FormData>
              control={form.control}
              errors={form.formState.errors.attendingClientIds}
              label={intl.formatMessage({ id: "forms.consultReport.clientAttendeesLabel" })}
              name="attendingClientIds"
              options={attendeeOptions}
              required
            />
            <Input<FormData>
              error={form.formState.errors.attendingOther}
              label={intl.formatMessage({ id: "forms.consultReport.otherLabel" })}
              register={form.register("attendingOther")}
            />
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.format}
              label={intl.formatMessage({ id: "forms.consultReport.formatLabel" })}
              name="format"
              options={formatOptions}
              required
            />
            <RichTextArea<FormData>
              control={form.control}
              error={form.formState.errors.summary}
              journeyId={journey.id}
              label={intl.formatMessage({ id: "forms.consultReport.summaryLabel" })}
              name="summary"
              required
            />
            <DateInput<FormData>
              control={form.control}
              error={form.formState.errors.nextConsult}
              futureOnly
              label={intl.formatMessage({ id: "forms.consultReport.nextConsultLabel" })}
              register={form.register("nextConsult")}
            />

            <FormButtons hookForm={form} onCancel={onCancel} />
          </Form>
        );
      }}
    </HandleQuery>
  );
};

export default ConsultReportForm;
